import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { Router } from "@angular/router";
import { LoginService } from "../../Services/login.service";
import {
  AngularFireDatabase,
  AngularFireList,
} from "@angular/fire/compat/database";
import { ProjectService } from "src/app/Services/project.service";
import { MessagingServiceService } from "src/app/Services/messaging-service.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import swal from "sweetalert2";
import { SharedService } from "src/app/Services/sharedService";
import { Observable, Subscription } from "rxjs";
import { data } from "jquery";
import { WelcomeComponent } from "src/app/welcome/welcome.component";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from "src/app/Services/auth.service";
import { TokenEncryptionService } from "src/app/Services/token-encryption.service";

class User {
  id: string;
  email: string;
  ssRequest: boolean;
  scRequestStart: boolean;
  scRequestStop: boolean;
}
@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.css"],
    standalone: false
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild('emailInput') emailInput?: ElementRef<HTMLInputElement>;
  type = 'login'
  loginForm: UntypedFormGroup;
  errorMessage: any;
  loading = false;
  private dbPath;
  usersRef: AngularFireList<User> = null;
  email: string;
  message: any;
  hide = true;
  emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  userDataSubscription: Subscription;
  isloading = false;
  visible = true;
  isDisable = false;
  featureImages = [
    { src: '../../../assets/images/MostUsedApplication.webp', className: 'most-used-application', classNameTop: 'flex_item_third_1', },
    { src: '../../../assets/images/workingHours.webp', className: 'working-hours', classNameTop: 'flex_item_third_2', },
    { src: '../../../assets/images/captureScreenShots.webp', className: 'capture-screenShots', classNameTop: 'flex_item_third_3', }
  ];

  constructor(
    private loginService: LoginService,
    private router: Router,
    private db: AngularFireDatabase,
    private _projectService: ProjectService,
    private messagingService: MessagingServiceService,
    private _snackBar: MatSnackBar,
    private _sharedServices: SharedService,
    public dialog: MatDialog,
    private authService: AuthService,
    private tokenEncryptionService: TokenEncryptionService
  ) //private socket: Socket
  {

    console.log(this.router.url); // Logs the current route URL

    this.type = 'login';


    messagingService.resetData();
    this._sharedServices.logInStateSubject.subscribe((result) => {
      if (result) {
        this.router.navigate(["/"]);
      }
    })
    this._sharedServices.sideMenuSubject.next(false);

    this.loginForm = new UntypedFormGroup({
      username: new UntypedFormControl("", [Validators.required]),
      password: new UntypedFormControl("", [Validators.required]),
    });

    // A previous login error is stale as soon as the person edits either field
    this.loginForm.valueChanges.subscribe(() => {
      if (this.errorMessage) {
        this.errorMessage = null;
      }
    });

  }

  create(user: User): any {
    return this.db.database.ref(this.dbPath).child(user.id).set(user);
  }

  userLogin() {
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.isloading = true;

    this.loginService.doLogin(this.loginForm.value).subscribe(
      (check: any) => {
        this.loading = true;
        this.email = check.user_email;
        this.isloading = false;

        // Check if user has permission to access Admin Panel
        // We want to explicitly block pure 'employee' roles
        // We cannot use a strict whitelist because new admins might have 'subscriber' 
        // or other WP default roles before full onboarding
        let hasEmployeeRole = false;
        let hasAdminOrManagerRole = false;
        
        const adminManagerRoles = ['administrator', 'company_admin', 'manager', 'lead', 'team_lead'];
        
        // Helper function to check roles
        const checkRoles = (roles: string | string[]) => {
          if (!roles) return;
          if (Array.isArray(roles)) {
            if (roles.some(r => r.toLowerCase() === 'employee')) hasEmployeeRole = true;
            if (roles.some(r => adminManagerRoles.includes(r.toLowerCase()))) hasAdminOrManagerRole = true;
          } else {
            if (roles.toLowerCase() === 'employee') hasEmployeeRole = true;
            if (adminManagerRoles.includes(roles.toLowerCase())) hasAdminOrManagerRole = true;
          }
        };
        
        // Check all possible locations for roles in the WP response
        checkRoles(check.roles);
        checkRoles(check.role);
        if (check.user) {
          checkRoles(check.user.roles);
          checkRoles(check.user.role);
        }

        // Block ONLY if they are an employee AND not also an admin/manager
        if (hasEmployeeRole && !hasAdminOrManagerRole) {
          this.loading = false;
          this.isloading = false;
          swal.fire({
            position: "center",
            icon: "error",
            title: "Access Denied",
            html: "General employees are not allowed to log into the Admin Panel. Please use the desktop application.",
          });
          return;
        }

        // Store local component state
        this.errorMessage = null;
        this.loading = false;
        this.dbPath = `/users/${check.company_id}`;

        // Check if email is verified (handle various truthy values: true, 'true', '1', 1)
        // Also handle falsy string values like 'false', '0' which are truthy in JS but mean false
        const isVerified = 
          check.is_verified === true || check.is_verified === 'true' || check.is_verified === '1' || check.is_verified === 1 ||
          check.email_verified === true || check.email_verified === 'true' || check.email_verified === '1' || check.email_verified === 1 ||
          check.verified === true || check.verified === 'true' || check.verified === '1' || check.verified === 1;
        
        const hasVerificationProperty = check.hasOwnProperty('verified') || check.hasOwnProperty('is_verified') || check.hasOwnProperty('email_verified');
        
        if (!isVerified && hasVerificationProperty) {
          // Email not verified - redirect to verification page
          this.tokenEncryptionService.setEncryptedItem('current_email', this.email);
          
          swal.fire({
            position: "center",
            title: "Verify your email",
            html: "<p>We sent a verification code. Enter it before you can sign in.</p>",
            confirmButtonText: "Enter verification code"
          }).then(() => {
            // Send verification code and redirect to verification page
            this._projectService.sendVarificationCode(this.email).subscribe(
              () => {
                this.router.navigate(["/verification-code"], { queryParams: { type: 'signup' } });
              },
              (err) => {
                this._snackBar.open(err.error?.message || 'Failed to send verification code', 'OK', {
                  duration: 3000
                });
                this.router.navigate(["/verification-code"], { queryParams: { type: 'signup' } });
              }
            );
          });
          return;
        }

        // Use the shared auth service to handle login (email is verified)
        this.authService.handleSuccessfulLogin(check, false, true);
      },
      (error) => {
        this.isloading = false;
        this.loading = false;
        // Handle different error structures: HTTP errors have error.error.message, thrown errors have error.message
        const errorMsg = error?.error?.message || error?.message || '';
        // A dropped connection or an unreachable server should never show technical text to the visitor
        const couldNotConnect = error?.status === 0 || /^http failure/i.test(String(errorMsg));
        // Shown inline as plain text: the server's message can contain HTML (WordPress adds <strong> and links)
        this.errorMessage = couldNotConnect
          ? "We couldn't connect. Check your connection and try again."
          : this.toPlainText(errorMsg) || 'We could not log you in. Please try again.';
      }
    );
  }

  ngAfterViewInit() {
    // Start typing straight away on desktop; on phones the keyboard would cover the page
    const canFocusAtOnce = window.matchMedia('(min-width: 961px) and (pointer: fine)').matches;
    if (canFocusAtOnce) {
      setTimeout(() => this.emailInput?.nativeElement.focus());
    }
  }

  private toPlainText(message: string): string {
    const doc = new DOMParser().parseFromString(String(message ?? ''), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  }

  ngOnInit() {
    this.loading = true;
    if (localStorage.getItem("logedInUser"))
      this.router.navigate(["/projects"]);
    this.loading = false;
  }
  goto(type) {
    // Simplified goto as signup page is separate
    if (type == 'signup') {
      this.router.navigate(["/signup"]);
    }
  }

  resendCode(check) {
    this
      ._projectService
      .sendVarificationCode(this.email).subscribe((user: any) => {
        if (user == true || user == 'true') {
          this.tokenEncryptionService.setEncryptedItem('current_email', this.email);

          this.errorMessage = null;
          this.loading = false;
          this.router.navigate(["/verification-code"], { queryParams: { type: 'login', data: JSON.stringify(check) } });

          this._snackBar.open('The recovery code sent to your email', 'Undo', {
            duration: 3000
          })
        }
      }, err => {
        this._snackBar.open(err.error.message, 'Undo', {
          duration: 3000
        })
      })

  }


  loginOnEnter(event: KeyboardEvent) {
    if (!this.isloading) {
      event.preventDefault();
      this.userLogin();
    }
  }
  openDialog(component): Observable<any> {
    const dialogRef = this.dialog.open(component, {
      width: '50%',
      height: '90vh'
    });
    return dialogRef.afterClosed();
  }
  openSupersee() {
    window.open('Https://supersee.io', '_blank')
  }
}
